/* globals JQuery */
import { Directive, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';

import { Helpers } from './helpers';

const defaultMessage = `<img id="blockUiImage" class="spinImage" alt="Loading..." src="assets/img/zb_spinner.svg">`;
const defaultCss = {};

export enum BlockOptionsType {
  Default,
  Login,
}

export class BlockOptions {
  message: string = defaultMessage;
  css: any = defaultCss;
  fadeIn?: number;
  fadeOut?: number;
  timeout?: number;
  centerY?: boolean = false;
  showOverlay?: boolean;
  theme?: boolean = false;
  themedCSS?: any = {
    width: '30%',
    top: '40%',
    left: '35%'
  };
  onUnblock?: (element: HTMLElement, unblockOptions?: any) => any;
}

export interface BlockableJQueryElement extends JQuery {
  block(options?: BlockOptions): void;
  unblock(): void;
}

/**
 * Block an element from interaction by the user.
 * Wraps the jQuery blockUI library. see {@link http://malsup.com/jquery/block/#element}
 *
 * Example usage:
 * @example
 * <div
 *    [zbpBlocked]="true"
 *    [zbpBlockedMessage]="This div is blocked."
 *    [zbpBlockedStyle]="{ \"color\":\"red\" }">
 * </div>
 */
@Directive({
  selector: '[zbpBlocked]',
  standalone: false
})
export class BlockedDirective implements OnChanges {
  /**
   * Array of properties that should be watched for changes to trigger block updates
   */
  private static inputProperties = ['zbpBlocked', 'zbpBlockedMessage', 'zbpBlockedStyles'];

  /**
   * The jQuery converted native element extended with blockUI.
   */
  private el: BlockableJQueryElement;

  /**
   * Flag for whether or not to block the element.
   */
  @Input() zbpBlocked: boolean;

  /**
   * [OPTIONAL] The message displayed to the user over the blocked element.
   */
  @Input() zbpBlockedMessage: string = defaultMessage;

  /**
   * [OPTIONAL] Any styles that should be applied to the message.
   * Must be a valid parsable JSON string. Example usage:
   * @example
   * "{ \"color\": \"red\" }"
   */
  @Input() zbpBlockedStyles: string = '{"width": "5rem"}';

  constructor(el: ElementRef) {
    this.el = $(el.nativeElement) as BlockableJQueryElement;
  }

  ngOnChanges(changes: SimpleChanges) {
    Object.keys(changes).forEach((propName) => {
      if (Object.prototype.hasOwnProperty.call(changes, propName)) {
        // If any of the blocked input properties are changed, we want to update the blocked status/message/style,
        if (BlockedDirective.inputProperties.indexOf(propName) !== -1
            && changes[propName].currentValue !== changes[propName].previousValue) {
          // Block is being turned off
          if (propName === 'zbpBlocked' && !changes[propName].currentValue) {
            this.el.unblock();
          // Either block is being set or it's already set and we're updating the message/style
          } else if ((propName === 'zbpBlocked' && changes[propName].currentValue) || this.zbpBlocked) {
            this.el.block({ message: this.zbpBlockedMessage, css: Helpers.tryParseJSON(this.zbpBlockedStyles) });
          }
        }
      }
    });
  }
}
